import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Code2,
  Rocket,
  Link2,
  RadioTower,
} from 'lucide-react';

const SERVICES = [
  {
    icon: Code2,
    name: 'Custom Software Products',
    desc: 'Tailor-made software built to your exact requirements. No compromises, no bloat.',
    badge: null,
  },
  {
    icon: Rocket,
    name: 'MVP & Prototype Development',
    desc: 'Quickly validate ideas with testable software — get to market before competitors notice.',
    badge: null,
  },
  {
    icon: Link2,
    name: 'Platform & Workflow Integration',
    desc: 'Seamless integration across platforms so your stack works like one product, not five.',
    badge: null,
  },
  {
    icon: RadioTower,
    name: 'AI & Automation Solutions',
    desc: 'Smart AI-Powered workflows that save time, reduce errors, and unlock new capabilities.',
    badge: 'NEW',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeUpDelayed = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 } },
};

const cardContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SectionEyebrow = ({ label }) => (
  <div className="svc-eyebrow">
    <span className="svc-eyebrow-line" />
    <span className="svc-eyebrow-text">{label}</span>
    <span className="svc-eyebrow-line" />
  </div>
);

const NewBadge = () => (
  <motion.span
    className="svc-badge"
    animate={{ scale: [1, 1.04, 1] }}
    transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
  >
    NEW
  </motion.span>
);

const ServiceCard = ({ service }) => {
  const Icon = service.icon;

  return (
    <motion.div className="svc-card" variants={cardVariants}>
      {service.badge && <NewBadge />}
      <div className="svc-icon-box">
        <Icon size={26} className="svc-icon" aria-hidden="true" />
      </div>
      <h3 className="svc-card-name">{service.name}</h3>
      <p className="svc-card-desc">{service.desc}</p>
    </motion.div>
  );
};

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });

  return (
    <section id="services" className="services-section" ref={sectionRef}>
      <div className="container">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={fadeUp}
        >
          <SectionEyebrow label="Services" />
        </motion.div>

        <motion.div
          className="svc-header"
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={fadeUpDelayed}
        >
          <h2 className="svc-heading">Our software product expertise</h2>
          <p className="svc-subtext">
            We build high-quality, scalable software products designed for real-world impact —
            from SaaS platforms to AI-powered workflows.
          </p>
        </motion.div>

        <motion.div
          className="svc-grid"
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={cardContainerVariants}
        >
          {SERVICES.map((service) => (
            <ServiceCard key={service.name} service={service} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
